import React from 'react';
import { CommentItem } from './CommentItem';
import { MessageSquare } from 'lucide-react';
import type { Comment } from '@/types/api';

interface CommentsListProps {
  comments: Comment[];
  articleSlug: string;
  isLoading: boolean;
}

// Helper function to organize comments into a tree structure
function organizeComments(comments: Comment[]): Comment[] {
  const commentMap = new Map<number, Comment & { replies: Comment[] }>();
  const rootComments: Comment[] = [];

  // First, create a map of all comments with empty replies arrays
  comments.forEach(comment => {
    commentMap.set(comment.id, { ...comment, replies: [] });
  });

  // Then, organize into tree structure
  comments.forEach(comment => {
    const commentWithReplies = commentMap.get(comment.id)!;

    if (comment.parent_id) {
      // This is a reply, add it to its parent's replies
      const parent = commentMap.get(comment.parent_id);
      if (parent) {
        parent.replies.push(commentWithReplies);
      }
    } else {
      // This is a root comment
      rootComments.push(commentWithReplies);
    }
  });

  return rootComments;
}

export function CommentsList({ comments, articleSlug, isLoading }: CommentsListProps) {
  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-muted rounded-full" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-muted rounded w-1/3" />
                <div className="h-4 bg-muted rounded w-2/3" />
                <div className="h-3 bg-muted rounded w-1/4" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!comments.length) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center text-muted-foreground">
        <MessageSquare className="h-12 w-12 mb-3 opacity-50" />
        <p className="text-sm">No comments yet</p>
        <p className="text-xs mt-1">Be the first to share your thoughts!</p>
      </div>
    );
  }

  const organizedComments = organizeComments(comments);

  return (
    <div className="p-4 space-y-6">
      {organizedComments.map((comment) => (
        <CommentItem
          key={comment.id}
          comment={comment}
          articleSlug={articleSlug}
        />
      ))}
    </div>
  );
}